import { Link } from 'react-router-dom';
import { TrendingUp, Cpu, Coins, Leaf, Crown, BadgeCheck, ChevronRight } from 'lucide-react';
import { usePageMeta, useFAQSchema } from '../../../hooks/useSEO';
import BreadcrumbSchema from '../../../components/seo/BreadcrumbSchema';
import { FAQSection, FinalCTA } from './DubaiCostOfLivingPage';

const SECTORS = [
  {
    field: 'AI & Robotics', icon: Cpu, salary: 'AED 18,000 – 30,000 / month',
    employers: 'G42, Microsoft AI, IBM Watson, Presight, Mubadala', pathway: 'Golden Visa — Specialist Talent',
    courses: 'MSc AI · MSc Data Science · MSc Robotics · BEng Computer Engineering',
    why: 'D33 has prioritised AI as one of three economic pillars. Dubai\u2019s G42 cluster alone is forecast to need 12,000+ AI engineers by 2030.',
  },
  {
    field: 'Fintech & Blockchain', icon: Coins, salary: 'AED 16,000 – 28,000 / month',
    employers: 'DIFC, ADGM, Mashreq Bank, Tabby, Postpay, Network International', pathway: '5-yr Green Visa → Golden Visa',
    courses: 'MSc Fintech · MSc Finance · MBA Finance · MSc Digital Currency',
    why: 'DIFC is the Middle East\u2019s #1 fintech hub. Dubai\u2019s ambition to attract 30+ unicorns by 2033 (D33 target) is fuelling sustained demand for product, analytics and risk talent.',
  },
  {
    field: 'Sustainable Energy', icon: Leaf, salary: 'AED 14,000 – 24,000 / month',
    employers: 'DEWA, Masdar, ENOC, ADNOC, Siemens Energy', pathway: '5-yr Green Visa → Golden Visa',
    courses: 'MSc Sustainable Energy · MSc Petroleum Engineering · BEng Renewable Energy',
    why: 'The UAE Net Zero 2050 strategy + Mohammed bin Rashid Al Maktoum Solar Park (largest single-site solar park globally) need 30,000+ clean-energy engineers this decade.',
  },
  {
    field: 'Luxury Hospitality', icon: Crown, salary: 'AED 12,000 – 22,000 / month',
    employers: 'Emaar Hospitality, Jumeirah, Atlantis, Four Seasons, Mandarin Oriental', pathway: '2-yr Work Visa → Green Visa',
    courses: 'BBA Hospitality · MSc Luxury Brand Management · MBA Hospitality',
    why: 'Dubai targets 40m tourists/year under D33. 100+ new luxury hotel keys are added every month, creating a permanent shortage of trained graduates in F&B, revenue and guest experience.',
  },
];

const FAQS = [
  { q: 'Which courses give the easiest path to a Dubai Golden Visa?', a: 'AI, Data Science, Fintech and Robotics — Dubai\u2019s Specialist Talent Golden Visa is heavily weighted toward graduates from these fields, especially with GPA 3.5+ and 1 year of experience inside a D33-priority employer.' },
  { q: 'Are Dubai jobs really tax-free for graduates?', a: 'Yes. The UAE imposes 0% personal income tax on resident employees. The 9% corporate tax (from June 2023) is paid by businesses on profits above AED 375,000 — it does not apply to your salary. Your gross pay = take-home pay.' },
  { q: 'Is hospitality a smart course choice given Dubai\u2019s tech push?', a: 'Yes — hospitality is one of the four D33-prioritised sectors. Dubai is adding 25,000+ new hotel rooms by 2030; demand for F&B managers, revenue analysts and luxury concierge graduates will outstrip supply for the entire decade.' },
  { q: 'How does the UAE Specialist Talent Golden Visa work?', a: 'It is a 10-year self-sponsored residency awarded to graduates and professionals in 6 priority domains: medicine, science, engineering, AI, big data, and digital innovation. You apply via the ICP portal with university attestation, a recommendation letter from your employer or a Dubai-regulated body, and proof of contribution to your field.' },
];

export default function DubaiTopCoursesPage() {
  usePageMeta(
    'Best Courses for Dubai Residency 2026 — D33 Skill Shortages & Golden Visa Pathways',
    'Choose a Dubai degree that maps to a D33 priority sector: AI & Robotics, Fintech, Sustainable Energy, Luxury Hospitality — full salary, employer and Golden Visa pathway breakdowns for Indian students.'
  );
  useFAQSchema(FAQS);

  return (
    <div className="zaf-edu-platform bg-white text-slate-900" data-testid="dubai-top-courses-page">
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://zafco.in/' },
        { name: 'Study Abroad', url: 'https://zafco.in/study-abroad/' },
        { name: 'Study in Dubai', url: 'https://zafco.in/study-abroad/dubai/' },
        { name: 'Top Courses for Residency', url: 'https://zafco.in/study-abroad/dubai/top-courses-for-residency/' },
      ]} />

      <section className="bg-gradient-to-br from-[#0F1B2C] to-[#162538] text-white py-12 md:py-16">
        <div className="container max-w-5xl mx-auto px-4 sm:px-6 md:px-12">
          <Link to="/study-abroad/dubai/" className="inline-flex items-center gap-2 text-[#D4AF37] text-xs font-semibold uppercase tracking-widest mb-4 hover:text-amber-300">
            <ChevronRight className="w-3 h-3 rotate-180" /> Dubai Hub
          </Link>
          <h1 className="text-3xl md:text-5xl font-bold font-syne leading-tight">Best Courses for Dubai Residency — 2026</h1>
          <p className="mt-4 text-slate-300 max-w-3xl leading-relaxed">Pick a degree that maps to a D33 priority sector — every field below is on the UAE&apos;s 2026 talent shortage list with an explicit <strong className="text-white">Golden Visa or Green Visa pathway</strong> for graduates.</p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container max-w-6xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-5">
            {SECTORS.map((s) => (
              <article key={s.field} className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#D4AF37] hover:shadow-md transition-all" data-testid={`sector-${s.field.split(' ')[0].toLowerCase()}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center"><s.icon className="w-6 h-6 text-[#D4AF37]" /></div>
                  <div>
                    <h2 className="font-bold font-syne text-xl text-[#0F1B2C]">{s.field}</h2>
                    <p className="text-xs text-slate-500 font-mono">{s.salary}</p>
                  </div>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed mb-4">{s.why}</p>

                <div className="space-y-2.5 text-sm">
                  <div className="flex flex-wrap gap-x-2 gap-y-1">
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Top Employers:</span>
                    <span className="text-slate-700">{s.employers}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-2 gap-y-1">
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Courses:</span>
                    <span className="text-slate-700">{s.courses}</span>
                  </div>
                  <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-800 text-xs font-semibold rounded-full px-3 py-1 border border-emerald-200">
                    <BadgeCheck className="w-3.5 h-3.5" /> {s.pathway}
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-9 p-5 bg-slate-50 border-l-4 border-[#0F1B2C] rounded-r-2xl flex gap-3">
            <TrendingUp className="w-5 h-5 text-[#0F1B2C] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-800 leading-relaxed"><strong>Counsellor verdict:</strong> Choose AI or Fintech if you want the fastest Golden Visa path. Choose Sustainable Energy or Luxury Hospitality if you want secure, decade-long demand with lower entry competition.</p>
          </div>
        </div>
      </section>

      <FAQSection faqs={FAQS} />
      <FinalCTA source="dubai-top-courses-residency" />
    </div>
  );
}
